import { MediaMatcher } from "@angular/cdk/layout";
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	Pipe,
	PipeTransform,
	Renderer2,
	SimpleChanges,
	ViewChild,
} from "@angular/core";
import { AbstractControl, UntypedFormBuilder } from "@angular/forms";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { IOrganization } from "../models/organization/IOrganization";
import { IPractice } from "../models/practice/IPractice";

// FIXME: There's a awful lot of duplication here, and it's likely to get worse if we were to add practice location.

/**
 * A component that used to allow a user to select a practice context. It provides selection for practice and experimentally selection for an organization. Note, however, that
 * the design of organization or organization select has not been finalized. This was an effort to ensure the component was designed in a way that would be mindful of other
 * contexts.
 *
 * While this can be used anywhere directly, it's intended to be used within the header component.
 *
 * Note that when the autocomplete boxes in this component are hidden, they're also disabled, which means they may not populate the way you would otherwise expect.
 */
@Component({
    // tslint:disable-next-line:component-selector
    selector: "ofi-foundation-practice-select",
    templateUrl: "./practice-select.component.html",
    styleUrls: ["./practice-select.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FoundationPracticeSelectComponent implements OnInit, OnChanges {
	/** Use behaviors to reduce the size of the fields on small screens. */
	@Input() adaptive = false;

	/** Should the practice select autocomplete be presented. */
	@Input() displayPractice = true;
	/** Should the organization select autocomplete be presented. */
	@Input() displayOrganization = false;
	/** The practice that's currently selected. */
	@Input() practices?: Array<IPractice> = [];
	/** The organization that's currently selected. */
	@Input() organizations?: Array<IOrganization> = [];
	/** The practice that's currently selected. */
	@Input() selectedPractice?: IPractice;
	/** The organization that's currently selected. */
	@Input() selectedOrganization?: IOrganization;
	/** Experimental: A flag to practices are in a loading state . */
	@Input() loadingPractices = false;
	/** Experimental: A flag to organizations are in a loading state . */
	@Input() loadingOrganizations = false;

	/** Emits the full practice object on selection. */
	@Output() practiceSelectionChange = new EventEmitter<IPractice>();
	/** Emits the full organization object on selection. */
	@Output() organizationSelectionChange = new EventEmitter<IOrganization>();

	/** A list of practices filtered based on what the user has typed in to the field. */
	filteredPractices: Observable<Array<IPractice>>;
	/** A list of practices filtered based on what the user has typed in to the field. */
	filteredOrganizations: Observable<Array<IOrganization>>;

	/** @ignore The media matcher for applying adaptive classes. */
	mediaQueryList: MediaQueryList;

	/** A reference to the form containing the selection field. */
	practiceSelectForm;
	/** A convenience getter for accessing the form from the template. */
	get fields(): { [key: string]: AbstractControl } {
		return this.practiceSelectForm.controls;
	}

	/** @ignore */
	constructor(private _renderer: Renderer2, private _formBuilder: UntypedFormBuilder, mediaMatcher: MediaMatcher) {
		this.mediaQueryList = mediaMatcher.matchMedia("(max-width: 450px)");
		this.practiceSelectForm = _formBuilder.group({
			organization: [undefined],
			practice: [undefined],
		});
	}

	/** @ignore */
	ngOnInit() {}

	/** @ignore */
	ngOnChanges(changes: SimpleChanges): void {
		// Disable when hidden
		if (changes.displayOrganization && changes.displayOrganization.currentValue === true) {
			this.fields.organization.enable();
		} else if (changes.displayOrganization && changes.displayOrganization.currentValue === false) {
			this.fields.organization.disable();
		}
		if (changes.displayPractice && changes.displayPractice.currentValue === true) {
			this.fields.practice.enable();
		} else if (changes.displayPractice && changes.displayPractice.currentValue === false) {
			this.fields.practice.disable();
		}
		if (this.displayOrganization && changes.selectedOrganization) {
			this.fields.organization.setValue(changes.selectedOrganization.currentValue);
			this.organizationSelectionChange.emit(changes.selectedOrganization.currentValue);
		}
		if (this.displayPractice && changes.selectedPractice) {
			this.fields.practice.setValue(changes.selectedPractice.currentValue);
			this.practiceSelectionChange.emit(changes.selectedPractice.currentValue);
		}

		// Disable when loading
		if (changes.loadingPractices && changes.loadingPractices.currentValue !== "undefined") {
			changes.loadingPractices.currentValue ? this.fields.practice.disable() : this.fields.practice.enable();
		}
		if (changes.loadingOrganizations && changes.loadingOrganizations.currentValue !== "undefined") {
			changes.loadingOrganizations.currentValue ? this.fields.organization.disable() : this.fields.organization.enable();
		}

		// The actual values to populate the dropdowns with:

		this.filteredPractices = this.practiceSelectForm.controls.practice.valueChanges.pipe(
			startWith(""),
			map((value: string | IPractice) => (typeof value === "string" ? value : value?.Name)),
			map((name: string) => (name ? this._filter(name, this.practices) : this.practices?.slice()))
		);

		this.filteredOrganizations = this.practiceSelectForm.controls.organization.valueChanges.pipe(
			startWith(""),
			map((value: string | IOrganization) => (typeof value === "string" ? value : value?.Name)),
			map((name: string) => (name ? this._filter(name, this.organizations) : this.organizations?.slice()))
		);
	}

	// TODO: Add filter for orgs if we decide to keep it in here.
	/** Filter a list of practices by name. */
	private _filter(needle: string, haystack: Array<IPractice | IOrganization>): Array<IPractice | IOrganization> {
		const filterValue = needle.toLowerCase();
		return haystack
			.filter((option) => option.Name.toLowerCase().indexOf(filterValue) >= 0)
			.sort((a, b) => {
				// If both are even, sort alphabetically. Otherwise, sort based on how early the search string appears.
				if (a.Name.toLowerCase().indexOf(filterValue) === b.Name.toLowerCase().indexOf(filterValue)) {
					return a.Name.localeCompare(b.Name);
				}
				return a.Name.toLowerCase().indexOf(filterValue) < b.Name.toLowerCase().indexOf(filterValue) ? -1 : 1;
				// TODO: The one with the most occurrences.
			});
	}

	/** Parse a Practice or Organization in to a value that can be displayed in the autocomplete */
	displayEntity(entity: IPractice | IOrganization) {
		return entity?.Name || undefined;
	}

	/** Used to emit an event when an organization is selected from the autocomplete */
	onOrganizationSelect(selection: IOrganization) {
		this.organizationSelectionChange.emit(selection);
		// TODO: On org change populate repopulate practice list.
	}

	/** Used to emit an event a practice is selected from the autocomplete */
	onPracticeSelect(selection: IPractice) {
		console.log("optionSelected", selection);
		this.practiceSelectionChange.emit(selection);
	}

	/** Add or remove a css class to the autocomplete wrapping element indicating the component should be full page. */
	onToggleFullpageAutocomplete(event: Event): void {
		const target = event.target as HTMLElement;
		const parentElement = target?.parentElement?.parentElement;

		if (parentElement) {
			if (parentElement.classList.contains("auto--is-fullpage")) {
				this._renderer.removeClass(parentElement, "auto--is-fullpage");
			} else {
				this._renderer.addClass(parentElement, "auto--is-fullpage");
			}
		} else {
			console.warn("Parent element not found");
		}
	}
}
